import { z } from 'zod';

import { CARD_INPUT_MAP_NAMES } from './input-maps.js';

export {
  CARD_INPUT_MAP_NAMES,
  CardInputMapError,
  applyCardInputMap,
  scheduleDayRuns,
} from './input-maps.js';
export type { CardInputMapName, CardScheduleGrid } from './input-maps.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KART EYLEMLERİ — sohbet kartındaki düğmenin sözleşmesi (Faz 8, 30 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürün kuralı (FAZ8-KART-TALIMAT §İlkeler 1): **kart düğmesi = insan kararı.**
 * Karttaki bir düğme modeli ÇAĞIRMAZ; sunucudaki eylem ucundan
 * (`POST /agent/cards/:callId/actions/:actionId`) kullanıcının OTURUMU ve
 * ROLÜYLE yürür:
 *
 *   platform_op → `PlatformOpsService.run` (origin `user`, idempotency, hesap
 *                 sahipliği, denetim — REST `/platform-ops/run` ile aynı kapı)
 *   app_op      → `AgentAppOpExecutor.apply` (Mentorivo ekran işlemi — onaylı
 *                 kartın yürütücüsüyle aynı)
 *   reply       → hiçbir şey yürütmez; seçim/metin karta yazılır ve modele
 *                 olay satırı olarak gider ("kullanıcı şunu seçti")
 *   link        → yalnız istemci: panel sayfasına ya da platformun resmî
 *                 belgesine gider; uca HİÇ gelmez (gelirse 400)
 *
 * 🔴 EYLEM TANIMI SUNUCUDA DOĞAR. Kart üreticisi (`apps/api/src/agent/cards/`,
 * araç `ui`su, `AppToolSpec.card`) eylemleri kartın `actions` alanına yazar;
 * kart sohbet satırına kaydedilir. Uç YALNIZ o kayıttaki eylemi çalıştırır:
 * istemci eylem kimliği ve (şemalı) girdi gönderir — `opId`, `tool`,
 * `params` istemciden ASLA gelmez. Kullanıcı girdisi (yanıt metni, tutar,
 * yüzde, seçim) aşağıdaki ADLI şemalardan geçer ve yalnız `bind` ile izin
 * verilen parametreye yazılır; seçim türü girdiler `choices` beyaz listesiyle
 * sınırlıdır.
 *
 * Otonomi politikası insan tıklamasına UYGULANMAZ (panelde bütçeyi elle
 * değiştirmek de politikaya takılmıyor); ROL uygulanır:
 *   · platform yazma → MEMBER (`campaign.write`); geri alınamaz / hassas /
 *     kişisel veri parametreli işlem → ADMIN (REST ile aynı çizgi)
 *   · ekran işlemi → sözlükteki `minRole` + `decisionRole`
 *   · `destructive: true` → ADMIN ve kart içi onay adımı
 *   · `minRole` → ek taban (yalnız YÜKSELTİR)
 * Etkin rol kart üretilirken hesaplanıp `minRole`e yazılır (ekran düğmeyi
 * doğru kişiye açsın); uç tıklamada AYNI fonksiyonla yeniden hesaplar.
 *
 * Sonuç (`CardActionRun`) kartın `actionState`ine yazılır — sayfa yenilenince
 * korunur — ve modele `[Card action — … not typed by the user]` olay satırı
 * gider. Teslim `followUp`a göre:
 *   · `turn` (varsayılan) → ekran hemen bir olay turu açar (bağlantı kartı
 *     deseni: `cardReply: { kind: 'action' }`). Olay turu günlük mesaj
 *     hakkını YAKMAZ (Faz 8 dalga 2 — `AgentChatService.eventTurnIsFree`:
 *     olay başına tek tur + kişi başına kısa pencere tavanı; tavanın üstü
 *     normal mesaj gibi haktan düşer, hak bitmişse olay bir sonraki mesaja
 *     biner). Eylemin kendisi zaten haktan bağımsız.
 *   · `next` → sessiz: olay kullanıcının BİR SONRAKİ mesajına eklenir (hak
 *     harcamaz, model araya girmez — gizle/beğen gibi küçük işler)
 *   · `none` → modele hiç gitmez (yalnız karttaki durum)
 *
 * 🔴 KİŞİSEL VERİ KARTA YAZILMAZ — `CardActionRun` da dahil. Kullanıcının
 * yazdığı yanıt metni (yorum yanıtı gibi) karta ve olay satırına GİRMEZ;
 * yalnız özeti (`digest`) durur. İstisna `reply` türü: kullanıcının modele
 * verdiği cevap (`value`, ≤400) bir sohbet mesajıyla aynı statüde.
 */

/** Eylem türleri. */
export const CARD_ACTION_KINDS = ['platform_op', 'app_op', 'reply', 'link'] as const;
export type CardActionKind = (typeof CARD_ACTION_KINDS)[number];

/** Sonucun modele teslimi — başlıktaki üç kip. */
export const CARD_ACTION_FOLLOW_UPS = ['turn', 'next', 'none'] as const;
export type CardActionFollowUp = (typeof CARD_ACTION_FOLLOW_UPS)[number];

/**
 * KULLANICI GİRDİSİ ŞEMALARI — adla seçilir, eylem tanımı şemayı DEĞİL adını
 * taşır (kart JSON'a yazılıyor; şema yazılamaz). Yeni bir girdi biçimi
 * gerekiyorsa buraya bir satır — sınırları dar tut, uç başka bir biçim kabul
 * etmez.
 *
 * `choice` ve `ids` eylemin `choices` listesiyle sınırlı (uç denetler).
 */
export const CARD_ACTION_INPUTS = {
  /** Serbest metin — yorum yanıtı, not. Karta/olay satırına YAZILMAZ (reply hariç). */
  text: z.object({ text: z.string().trim().min(1).max(8000) }).strict(),
  /** Tutar (ANA birim, hesabın para birimi). */
  amount: z.object({ amount: z.number().finite().positive().max(1_000_000_000) }).strict(),
  /** Yüzde değişim (−95 … +1000) — `bind` `percentOf` ile tutara çevirir. */
  percent: z.object({ percent: z.number().finite().min(-95).max(1000) }).strict(),
  /** Tek seçim — `choices` içinden. */
  choice: z.object({ value: z.string().trim().min(1).max(200) }).strict(),
  /** Çoklu seçim — `choices` içinden, tekrarsız. */
  ids: z
    .object({ ids: z.array(z.string().trim().min(1).max(200)).min(1).max(200) })
    .strict()
    .refine((value) => new Set(value.ids).size === value.ids.length, { message: 'duplicate ids' }),
  /** 7×24 gün-saat ızgarası (0/1) — reklam takvimi. Gün 0 = Pazartesi. */
  grid: z
    .object({
      grid: z.array(z.array(z.union([z.literal(0), z.literal(1)])).length(24)).length(7),
    })
    .strict(),
} as const;
export type CardActionInputName = keyof typeof CARD_ACTION_INPUTS;
export const CARD_ACTION_INPUT_NAMES = Object.keys(CARD_ACTION_INPUTS) as CardActionInputName[];
export type CardActionInput<N extends CardActionInputName = CardActionInputName> = z.infer<
  (typeof CARD_ACTION_INPUTS)[N]
>;

/**
 * Girdinin parametreye bağlanışı. Anahtar girdi alanı (`text`, `amount`,
 * `percent`, `value`, `ids`, `grid`); değer parametre YOLU (`a.b` iç içe) ya da
 * yüzde → tutar dönüşümü: `{ param, percentOf, round }` →
 * `param = round(percentOf × (1 + percent/100))` ya da adlı dönüştürücü:
 * `{ param, map }` → `param = CARD_INPUT_MAPS[map](girdi)` (`./input-maps.ts`).
 */
export const cardActionBindSchema = z.record(
  z.string().regex(/^[a-z]{1,16}$/),
  z.union([
    z.string().regex(/^[A-Za-z0-9_]{1,64}(\.[A-Za-z0-9_]{1,64}){0,3}$/),
    z
      .object({
        param: z.string().regex(/^[A-Za-z0-9_]{1,64}(\.[A-Za-z0-9_]{1,64}){0,3}$/),
        percentOf: z.number().finite().positive(),
        round: z.number().int().min(0).max(4).optional(),
      })
      .strict(),
    /* Adlı dönüştürücü (`./input-maps.ts`) — girdi biçimi platformunkinden
       farklıysa (7×24 takvim ızgarası → Meta dilimleri / Google aralıkları /
       TikTok 336 karakter). Sunucu tıklamada uygular; istemci parametre yazmaz. */
    z
      .object({
        param: z.string().regex(/^[A-Za-z0-9_]{1,64}(\.[A-Za-z0-9_]{1,64}){0,3}$/),
        map: z.enum(CARD_INPUT_MAP_NAMES),
      })
      .strict(),
  ]),
);

const ROLE = z.enum(['OWNER', 'ADMIN', 'MEMBER', 'VIEWER']);

/**
 * TEK EYLEMİN TANIMI — kartın `actions` dizisinin öğesi.
 *
 * Kart üreticisi yazar, sunucu (`finalizeCard`) şemadan geçirir ve etkin rolü
 * `minRole`e işler; geçmeyen eylem karttan DÜŞER (kart çizilir, düğme yok).
 */
export const cardActionSchema = z
  .object({
    /** Kart içinde benzersiz. Satır eylemi için `<eylem>:<satır>` biçimi önerilir. */
    id: z.string().regex(/^[A-Za-z0-9_.:-]{1,96}$/),
    kind: z.enum(CARD_ACTION_KINDS),
    /**
     * Düğme metninin ANAHTARI — `dashboard.agentChat.cards.action.label.<label>`
     * (sunucu anahtar gönderir, ekran çevirir). Yoksa ekran genel "Uygula"ya düşer.
     */
    label: z.string().regex(/^[a-z0-9_]{1,48}$/),
    labelParams: z.record(z.string(), z.union([z.string().max(120), z.number()])).optional(),
    /** `platform_op`: katalog operasyonu (`meta.campaign.update` …). */
    opId: z.string().min(3).max(120).optional(),
    /** `app_op`: yazma türü ekran aracı (`AGENT_APP_TOOLS`). */
    tool: z.string().min(3).max(64).optional(),
    /** SABİT parametreler — sunucu yazdı, istemci değiştiremez. Kişisel veri GİRMEZ. */
    params: z.record(z.string(), z.unknown()).optional(),
    /** Kullanıcı girdisi şemasının ADI (`CARD_ACTION_INPUTS`). */
    input: z.enum(Object.keys(CARD_ACTION_INPUTS) as [CardActionInputName, ...CardActionInputName[]]).optional(),
    /** Girdi → parametre bağlaması (yukarıda). `input` varsa ZORUNLU (reply hariç). */
    bind: cardActionBindSchema.optional(),
    /** `choice` / `ids` girdisinin beyaz listesi. */
    choices: z.array(z.string().min(1).max(200)).max(200).optional(),
    /** Yıkıcı (silme, gizleme, kesme…): ADMIN tabanı + kart içi onay adımı. */
    destructive: z.boolean().optional(),
    /** Yıkıcı değil ama ağır (bütçe, yayına alma): kart içi onay adımı. */
    confirm: z.boolean().optional(),
    /** Ek rol tabanı; `finalizeCard` etkin rolü buraya yazar. */
    minRole: ROLE.optional(),
    /** Eylemin ait olduğu satır/öğe kimliği — görünüm düğmeyi o satıra koyar. */
    scope: z.string().min(1).max(160).optional(),
    /** Başarıyla bittikten sonra yeniden basılabilir mi (bütçe ±%, yanıt). */
    repeatable: z.boolean().optional(),
    /** Sonucun modele teslimi — varsayılan `turn`. */
    followUp: z.enum(CARD_ACTION_FOLLOW_UPS).optional(),
    /** `link`: `/dashboard…` panel yolu ya da `https://` resmî adres. */
    href: z.string().min(1).max(500).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const need = (ok: boolean, message: string) => {
      if (!ok) ctx.addIssue({ code: 'custom', message });
    };
    if (value.kind === 'platform_op') need(Boolean(value.opId) && !value.tool && !value.href, 'platform_op needs opId only');
    if (value.kind === 'app_op') need(Boolean(value.tool) && !value.opId && !value.href, 'app_op needs tool only');
    if (value.kind === 'reply') need(!value.opId && !value.tool && !value.href, 'reply takes no target');
    if (value.kind === 'link') {
      need(Boolean(value.href) && !value.opId && !value.tool && !value.input, 'link needs href only');
      need(
        !value.href || /^\/dashboard(\/|$)/.test(value.href) || /^https:\/\/[^\s]+$/.test(value.href),
        'link href must be a /dashboard path or an https address',
      );
    }
    if (value.input && value.kind !== 'reply' && value.kind !== 'link') {
      need(Boolean(value.bind && Object.keys(value.bind).length > 0), 'input needs bind');
    }
    if (value.input === 'choice' || value.input === 'ids') {
      need(Boolean(value.choices && value.choices.length > 0), `${value.input} input needs choices`);
    }
  });
export type CardAction = z.infer<typeof cardActionSchema>;

/**
 * BİR EYLEMİN SONUCU — kartın `actionState[<eylem>]`i.
 *
 * 🔴 Kişisel veri YOK: yalnız durum, kod, tek satır özet (kişisel veri
 * operasyonunda özet de yazılmaz), oluşan/değişen nesnelerin kimlikleri.
 */
export interface CardActionRun {
  status: 'done' | 'failed';
  /** ISO — son deneme. */
  at: string;
  /** Girdinin özeti (SHA-256, 16 hex) — idempotency ve "aynı girdi mi". */
  digest: string;
  /** Başarısızlıkta hata KODU — ekran `cards.error.<code>` ile çevirir. */
  code: string | null;
  /** İşlemin tek satırlık İngilizce özeti — ekrana değil modele (≤300). */
  summary: string | null;
  /** Zinciri sürdürecek kimlikler (platform nesneleri, Mentorivo kayıtları). */
  result: {
    created?: { level: string; platformRef: string }[];
    changed?: { level: string; platformRef: string }[];
    ids?: Record<string, string | number | boolean | null | string[]>;
  } | null;
  /** Yalnız `reply`: kullanıcının karttan verdiği cevap (seçim ya da ≤400 metin). */
  value: string | null;
  /** Olay satırı modele ulaştı mı (`turn`/`next` teslim edildiğinde `true`). */
  reported: boolean;
}

/**
 * EYLEM TAŞIYAN HER KARTIN ORTAK ALANLARI — alan kartları bu tiple kesişir
 * (`Actionable<…>`). Üçü de sunucuda yazılır; istemci yalnız okur.
 */
export interface CardActionFields {
  actions?: CardAction[];
  /** Eylem kimliği → son sonuç. */
  actionState?: Record<string, CardActionRun>;
  /**
   * Modele henüz ulaşmamış olay var — `jsonb @>` ile bulunur
   * (`AgentConversationService.takeCardEvents`); teslimde `false`a döner.
   */
  eventPending?: boolean;
}

export type Actionable<T> = T & CardActionFields;

/** `POST /agent/cards/:callId/actions/:actionId` gövdesi. */
export const cardActionRequestSchema = z
  .object({
    /// Kartın sohbeti — sahiplik (`workspaceId` + `userId`) bununla doğrulanır.
    conversationId: z.uuid(),
    /// Eylemin `input` şemasına uyan nesne; eylem girdi almıyorsa GÖNDERİLMEZ.
    input: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type CardActionRequest = z.infer<typeof cardActionRequestSchema>;

/** Uç yanıtı — kartın TAZE hâli (yerel durum bununla değişir). */
export interface CardActionResponse<C = unknown> {
  card: C;
  actionId: string;
  run: CardActionRun;
  /** Ekranın yapacağı: `turn` → olay turunu aç; diğerleri → hiçbir şey. */
  followUp: CardActionFollowUp;
}

/** `GET /agent/cards/:callId/data?conversationId=` yanıtı — KAYDEDİLMEZ. */
export interface CardLiveDataResponse<D = unknown> {
  kind: string;
  toolCallId: string;
  /** ISO — ekran "az önce" damgası basabilir. */
  fetchedAt: string;
  data: D;
}

/** Uçların sorgu şeması (canlı veri). */
export const cardLiveDataQuerySchema = z.object({ conversationId: z.uuid() }).strict();

/** Kartın eylem listesi — eylem taşımayan türde boş dizi. */
export function cardActionsOf(card: unknown): CardAction[] {
  if (!card || typeof card !== 'object') return [];
  const actions = (card as CardActionFields).actions;
  return Array.isArray(actions) ? actions : [];
}

/** Kartın eylem sonuçları — yoksa boş nesne. */
export function cardActionStateOf(card: unknown): Record<string, CardActionRun> {
  if (!card || typeof card !== 'object') return {};
  const state = (card as CardActionFields).actionState;
  return state && typeof state === 'object' ? state : {};
}
